// AdminPage.jsx - Refonte avec approche par page et gestion des rôles
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Package, 
  Users, 
  Handshake, 
  Calendar, 
  Image as ImageIcon,
  LogOut,
  Loader2,
  Award,
  Mail,
  MessageSquare,
  Database,
  Server,
  Globe,
  MessageCircle,
  Star,
  Settings,
  Home,
  AlertTriangle,
  Plus,
  ArrowRight,
  Briefcase,
  Phone,
  FileText,
  SlidersHorizontal,
  Palette,
  Share2,
  Shield,
  Bell,
  User,
  Key
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ChangePasswordModal, ForceChangePasswordModal } from '../components/PasswordModals';

// Import all admin managers
import {
  PageImagesManager,
  ContentManager,
  PartnersManager,
  ReferencesManager,
  EventsManager,
  EquipmentManager,
  HeroSlidesManager,
  FooterManager,
  ContactInfoManager,
  MessagesManager,
  ChatManager,
  UsersManager,
  FeedbacksManager,
  ServicesManager,
  SocialManager,
  RecaptchaManager,
  EventAlertManager
} from '../components/admin';
import BackupManager from '../components/admin/BackupManager';
import SystemManager from '../components/admin/SystemManager';
import LogoManager from '../components/admin/LogoManager';
import NotificationManager from '../components/admin/NotificationManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============== DASHBOARD COMPONENT ==============
const Dashboard = ({ onNavigate, hasPermission, userRole }) => {
  const [stats, setStats] = useState({ partners: 0, equipment: 0, references: 0, events: 0, messages: 0, feedbacks: 0 });
  
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [partnersRes, equipmentRes, referencesRes, eventsRes, messagesRes, feedbacksRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/partners`, { credentials: 'include' }),
          fetch(`${BACKEND_URL}/api/equipment-categories`, { credentials: 'include' }),
          fetch(`${BACKEND_URL}/api/references`, { credentials: 'include' }),
          fetch(`${BACKEND_URL}/api/events`, { credentials: 'include' }),
          fetch(`${BACKEND_URL}/api/admin/contacts`, { credentials: 'include' }),
          fetch(`${BACKEND_URL}/api/admin/feedbacks`, { credentials: 'include' })
        ]);
        const [partners, equipment, references, events, messages, feedbacks] = await Promise.all([
          partnersRes.json(), equipmentRes.json(), referencesRes.json(), eventsRes.json(), messagesRes.json(), feedbacksRes.json()
        ]);
        setStats({
          partners: partners?.length || 0, equipment: equipment?.length || 0, references: references?.length || 0,
          events: events?.length || 0, messages: messages?.length || 0, feedbacks: feedbacks?.length || 0
        });
      } catch (err) { console.error('Error fetching stats:', err); }
    };
    fetchStats();
  }, []);

  const statCards = [
    { label: 'Partenaires', value: stats.partners, icon: Handshake, color: 'bg-blue-500', tab: 'page-partners' },
    { label: 'Équipements', value: stats.equipment, icon: Package, color: 'bg-green-500', tab: 'page-equipment' },
    { label: 'Références', value: stats.references, icon: Award, color: 'bg-purple-500', tab: 'page-references' },
    { label: 'Événements', value: stats.events, icon: Calendar, color: 'bg-orange-500', tab: 'page-events' },
    { label: 'Messages', value: stats.messages, icon: Mail, color: 'bg-pink-500', tab: 'messages' },
    { label: 'Demandes', value: stats.feedbacks, icon: MessageSquare, color: 'bg-teal-500', tab: 'feedbacks' },
  ];

  // Filter stat cards based on permissions
  const visibleStatCards = statCards.filter(card => hasPermission(card.tab));

  // Quick actions based on role
  const allQuickActions = [
    { label: 'Modifier l\'accueil', icon: Home, tab: 'page-home', color: 'text-blue-600 bg-blue-50', adminOnly: true },
    { label: 'Ajouter un partenaire', icon: Plus, tab: 'page-partners', color: 'text-green-600 bg-green-50', adminOnly: true },
    { label: 'Gérer les équipements', icon: Package, tab: 'page-equipment', color: 'text-purple-600 bg-purple-50' },
    { label: 'Voir les messages', icon: Mail, tab: 'messages', color: 'text-pink-600 bg-pink-50' },
    { label: 'Voir les événements', icon: Calendar, tab: 'page-events', color: 'text-orange-600 bg-orange-50' },
    { label: 'Avis Google', icon: Star, tab: 'reviews', color: 'text-yellow-600 bg-yellow-50' },
  ];

  // Filter quick actions based on permissions
  const quickActions = allQuickActions.filter(action => hasPermission(action.tab));

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold text-slate-800 mb-2">Tableau de Bord</h2>
        <p className="text-slate-500">Vue d'ensemble de votre site</p>
        {userRole === 'user' && (
          <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full">
            <User className="w-4 h-4" />
            Accès Utilisateur (limité)
          </div>
        )}
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {visibleStatCards.map((card) => (
          <button 
            key={card.label} 
            onClick={() => onNavigate(card.tab)}
            className="bg-white rounded-xl p-4 border border-slate-200 hover:shadow-md hover:border-slate-300 transition-all text-left"
          >
            <div className={`w-10 h-10 ${card.color} rounded-lg flex items-center justify-center mb-3`}>
              <card.icon className="w-5 h-5 text-white" />
            </div>
            <p className="text-2xl font-bold text-slate-800">{card.value}</p>
            <p className="text-sm text-slate-500">{card.label}</p>
          </button>
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="text-lg font-medium text-slate-800 mb-4">Actions rapides</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {quickActions.slice(0, 4).map((action) => (
            <button
              key={action.label}
              onClick={() => onNavigate(action.tab)}
              className={`${action.color} rounded-xl p-4 hover:shadow-md transition-all flex items-center gap-3`}
            >
              <action.icon className="w-5 h-5" />
              <span className="font-medium text-sm">{action.label}</span>
              <ArrowRight className="w-4 h-4 ml-auto opacity-50" />
            </button>
          ))}
        </div>
      </div>

      {/* Tips */}
      {userRole === 'admin' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800">Notes importantes</h4>
              <ul className="text-sm text-amber-700 mt-2 space-y-1">
                <li>• Les modifications de <strong>Paramètres Système</strong> nécessitent un redémarrage du serveur</li>
                <li>• Les images sont automatiquement compressées avant upload</li>
                <li>• Faites des sauvegardes régulières de la base de données</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== SECTION WRAPPER ==============
const Section = ({ title, icon: Icon, children, note }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
      <div className="flex items-center gap-3">
        {Icon && <Icon className="w-5 h-5 text-slate-600" />}
        <h3 className="font-semibold text-slate-800">{title}</h3>
      </div>
      {note && <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">{note}</span>}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

// ============== PAGE EDITORS ==============

// Page Accueil Editor
const HomePageEditor = () => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-semibold text-slate-800 mb-2">Page Accueil</h2>
      <p className="text-slate-500">Modifiez les sections visibles sur la page d'accueil</p>
    </div>
    
    <Section title="Slider Hero" icon={SlidersHorizontal}>
      <HeroSlidesManager />
    </Section>
    
    <Section title="Textes & Contenu" icon={FileText}>
      <ContentManager />
    </Section>
  </div>
);

// Page Équipements Editor
const EquipmentPageEditor = () => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-semibold text-slate-800 mb-2">Page Équipements</h2>
      <p className="text-slate-500">Modifiez les catégories et pièces d'équipement</p>
    </div>
    
    <Section title="Catégories d'équipements" icon={Package}>
      <EquipmentManager />
    </Section>
  </div>
);

// Page Services Editor  
const ServicesPageEditor = () => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-semibold text-slate-800 mb-2">Page Services</h2>
      <p className="text-slate-500">Gérez les services affichés sur la page</p>
    </div>
    
    <Section title="Liste des services" icon={Briefcase}>
      <ServicesManager />
    </Section>
  </div>
);

// Page Partenaires Editor
const PartnersPageEditor = () => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-semibold text-slate-800 mb-2">Page Partenaires</h2>
      <p className="text-slate-500">Gérez vos partenaires commerciaux</p>
    </div>
    
    <Section title="Liste des partenaires" icon={Handshake}>
      <PartnersManager />
    </Section>
  </div>
);

// Page Références Editor
const ReferencesPageEditor = () => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-semibold text-slate-800 mb-2">Page Références</h2>
      <p className="text-slate-500">Gérez vos références clients</p>
    </div>
    
    <Section title="Liste des références" icon={Award}>
      <ReferencesManager />
    </Section>
  </div>
);

// Page Événements Editor
const EventsPageEditor = () => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-semibold text-slate-800 mb-2">Page Événements</h2>
      <p className="text-slate-500">Gérez vos événements passés et à venir</p>
    </div>
    
    <Section title="Alertes événements" icon={Bell}>
      <EventAlertManager />
    </Section>
    
    <Section title="Liste des événements" icon={Calendar}>
      <EventsManager />
    </Section>
  </div>
);

// Page Contact Editor
const ContactPageEditor = () => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-semibold text-slate-800 mb-2">Page Contact</h2>
      <p className="text-slate-500">Informations de contact affichées sur la page</p>
    </div>
    
    <Section title="Informations de contact" icon={Phone}>
      <ContactInfoManager />
    </Section>
  </div>
);

// ============== MAIN ADMIN PAGE ==============
const AdminPage = () => {
  const { user, isAuthenticated, logout, hasPermission, userRole, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showForceChangePassword, setShowForceChangePassword] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      if (!isAuthenticated) {
        navigate('/login');
        return;
      }
      setIsLoading(false);
      
      // Check if must change password (for local auth users)
      if (user?.must_change_password && user?.auth_type === 'local') {
        setShowForceChangePassword(true);
      }
    };
    checkAuth();
  }, [isAuthenticated, navigate, user]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handlePasswordChanged = async () => {
    setShowForceChangePassword(false);
    setShowChangePassword(false);
    await refreshUser();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Menu organized by purpose with permissions
  const menuCategories = [
    {
      title: 'Général',
      items: [
        { id: 'dashboard', label: 'Tableau de Bord', icon: LayoutDashboard },
      ]
    },
    {
      title: 'Pages du Site',
      adminOnly: false,
      items: [
        { id: 'page-home', label: 'Accueil', icon: Home, adminOnly: true },
        { id: 'page-equipment', label: 'Équipements', icon: Package },
        { id: 'page-services', label: 'Services', icon: Briefcase, adminOnly: true },
        { id: 'page-partners', label: 'Partenaires', icon: Handshake, adminOnly: true },
        { id: 'page-references', label: 'Références', icon: Award, adminOnly: true },
        { id: 'page-events', label: 'Événements', icon: Calendar },
        { id: 'page-contact', label: 'Contact', icon: Phone },
      ]
    },
    {
      title: 'Apparence Globale',
      adminOnly: true,
      items: [
        { id: 'branding', label: 'Logos & Favicon', icon: ImageIcon },
        { id: 'page-images', label: 'Images des Pages', icon: Palette },
        { id: 'footer', label: 'Pied de Page', icon: Globe },
        { id: 'social', label: 'Réseaux Sociaux', icon: Share2 },
      ]
    },
    {
      title: 'Services Externes',
      items: [
        { id: 'reviews', label: 'Avis Google', icon: Star },
        { id: 'chat', label: 'Chat Tawk.to', icon: MessageCircle, adminOnly: true },
        { id: 'recaptcha', label: 'reCAPTCHA', icon: Shield, adminOnly: true },
      ]
    },
    {
      title: 'Communications',
      items: [
        { id: 'messages', label: 'Messages Reçus', icon: Mail },
        { id: 'feedbacks', label: 'Demandes Partenariat', icon: MessageSquare },
        { id: 'notifications', label: 'Emails Notification', icon: Settings, adminOnly: true },
      ]
    },
    {
      title: 'Administration',
      adminOnly: true,
      items: [
        { id: 'users', label: 'Utilisateurs', icon: Users },
        { id: 'backup', label: 'Sauvegarde', icon: Database },
        { id: 'system', label: 'Système', icon: Server },
      ]
    }
  ];

  // Filter menu items based on user role
  const filteredMenuCategories = menuCategories
    .filter(category => !category.adminOnly || userRole === 'admin')
    .map(category => ({
      ...category,
      items: category.items.filter(item => !item.adminOnly || hasPermission(item.id))
    }))
    .filter(category => category.items.length > 0);

  const renderContent = () => {
    // Check permission before rendering
    if (!hasPermission(activeTab) && activeTab !== 'dashboard') {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Shield className="w-16 h-16 text-slate-300 mb-4" />
          <h3 className="text-xl font-medium text-slate-600 mb-2">Accès non autorisé</h3>
          <p className="text-slate-500">Vous n'avez pas les permissions pour accéder à cette section.</p>
          <Button onClick={() => setActiveTab('dashboard')} className="mt-4">
            Retour au tableau de bord
          </Button>
        </div>
      );
    }

    switch (activeTab) {
      case 'dashboard':
        return <Dashboard onNavigate={setActiveTab} hasPermission={hasPermission} userRole={userRole} />;
      
      // Pages du site
      case 'page-home':
        return <HomePageEditor />;
      case 'page-equipment':
        return <EquipmentPageEditor />;
      case 'page-services':
        return <ServicesPageEditor />;
      case 'page-partners':
        return <PartnersPageEditor />;
      case 'page-references':
        return <ReferencesPageEditor />;
      case 'page-events':
        return <EventsPageEditor />;
      case 'page-contact':
        return <ContactPageEditor />;
      
      // Apparence
      case 'branding':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-800 mb-2">Logos & Favicon</h2>
              <p className="text-slate-500">Personnalisez l'identité visuelle de votre site</p>
            </div>
            <Section title="Logos du site" icon={ImageIcon}>
              <LogoManager mode="logos" />
            </Section>
          </div>
        );
      case 'footer':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-800 mb-2">Pied de Page</h2>
              <p className="text-slate-500">Gérez le contenu du footer</p>
            </div>
            <FooterManager />
          </div>
        );
      case 'page-images':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-800 mb-2">Images des Pages</h2>
              <p className="text-slate-500">Gérez les images de fond et d'illustration des différentes pages</p>
            </div>
            <PageImagesManager />
          </div>
        );
      case 'social':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-800 mb-2">Réseaux Sociaux</h2>
              <p className="text-slate-500">Configurez vos liens réseaux sociaux (footer et page contact)</p>
            </div>
            <Section title="Configuration des réseaux" icon={Share2}>
              <SocialManager />
            </Section>
          </div>
        );
      
      // Services externes
      case 'reviews':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-800 mb-2">Avis Google</h2>
              <p className="text-slate-500">Configurez le bandeau d'avis Google</p>
            </div>
            <Section title="Paramètres des avis" icon={Star}>
              <LogoManager mode="reviews" />
            </Section>
          </div>
        );
      case 'chat':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-800 mb-2">Chat Tawk.to</h2>
              <p className="text-slate-500">Configurez le widget de chat en direct</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-amber-700">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                Rechargez la page après modification pour voir les changements
              </p>
            </div>
            <Section title="Configuration du chat" icon={MessageCircle}>
              <ChatManager />
            </Section>
          </div>
        );
      case 'recaptcha':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-800 mb-2">reCAPTCHA</h2>
              <p className="text-slate-500">Protection anti-robot sur les formulaires</p>
            </div>
            <Section title="Configuration reCAPTCHA" icon={Shield}>
              <RecaptchaManager />
            </Section>
          </div>
        );
      
      // Communications
      case 'messages':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-800 mb-2">Messages Reçus</h2>
              <p className="text-slate-500">Messages envoyés via le formulaire de contact</p>
            </div>
            <MessagesManager />
          </div>
        );
      case 'feedbacks':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-800 mb-2">Demandes de Partenariat</h2>
              <p className="text-slate-500">Demandes reçues via le formulaire partenariat</p>
            </div>
            <FeedbacksManager />
          </div>
        );
      case 'notifications':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-800 mb-2">Emails de Notification</h2>
              <p className="text-slate-500">Configurez les destinataires des emails</p>
            </div>
            <Section title="Adresses email" icon={Mail}>
              <NotificationManager />
            </Section>
          </div>
        );
      
      // Administration
      case 'users':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-800 mb-2">Utilisateurs Admin</h2>
              <p className="text-slate-500">Gérez les administrateurs du site</p>
            </div>
            <UsersManager />
          </div>
        );
      case 'backup':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-800 mb-2">Sauvegarde Base de Données</h2>
              <p className="text-slate-500">Créez et restaurez des sauvegardes</p>
            </div>
            <BackupManager />
          </div>
        );
      case 'system':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-800 mb-2">Paramètres Système</h2>
              <p className="text-slate-500">Configuration avancée du serveur</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-red-800">Attention - Zone sensible</h4>
                  <p className="text-sm text-red-700">
                    Les modifications ici nécessitent un <strong>redémarrage du serveur</strong> pour prendre effet.
                  </p>
                </div>
              </div>
            </div>
            <SystemManager />
          </div>
        );
      
      default:
        return <Dashboard onNavigate={setActiveTab} hasPermission={hasPermission} userRole={userRole} />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white fixed h-full flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-slate-700 flex-shrink-0">
          <h1 className="text-xl font-bold">SAE - Gestion</h1>
          <p className="text-sm text-slate-400 mt-1">Panneau d'administration</p>
        </div>
        
        {/* User */}
        <div className="px-4 py-3 border-b border-slate-700 flex items-center gap-3 flex-shrink-0">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
              userRole === 'admin' ? 'bg-purple-600' : 'bg-blue-600'
            }`}>
              {userRole === 'admin' ? (
                <Shield className="w-5 h-5" />
              ) : (
                <User className="w-5 h-5" />
              )}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name || 'Admin'}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            <span className={`text-xs px-2 py-0.5 rounded-full mt-1 inline-block ${
              userRole === 'admin' ? 'bg-purple-500/30 text-purple-300' : 'bg-blue-500/30 text-blue-300'
            }`}>
              {userRole === 'admin' ? 'Admin' : 'Utilisateur'}
            </span>
          </div>
        </div>

        {/* Menu - Scrollable */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-5">
          {filteredMenuCategories.map((category) => (
            <div key={category.title}>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-3">
                {category.title}
              </h3>
              <div className="space-y-1">
                {category.items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeTab === item.id
                        ? 'bg-blue-600 text-white'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                  >
                    <item.icon className="w-4 h-4 flex-shrink-0" />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Logout - Fixed at bottom */}
        <div className="p-4 border-t border-slate-700 flex-shrink-0 space-y-2">
          {/* Change Password button for local auth users */}
          {user?.auth_type === 'local' && (
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
              onClick={() => setShowChangePassword(true)}
            >
              <Key className="w-4 h-4 mr-3" />
              Changer mot de passe
            </Button>
          )}
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-3" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        <div className="max-w-6xl mx-auto">
          {renderContent()}
        </div>
      </main>

      {/* Password Modals */}
      <ChangePasswordModal 
        isOpen={showChangePassword} 
        onClose={() => setShowChangePassword(false)}
        onSuccess={handlePasswordChanged}
      />
      <ForceChangePasswordModal 
        isOpen={showForceChangePassword}
        onSuccess={handlePasswordChanged}
      />
    </div>
  );
};

export default AdminPage;
